import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Slide, Stack } from '@mui/material';
import Header from '@shared/shell/Layout/Header';
import { useConnections } from '@shared/hooks';
import { useTutorialStore } from '../../../application/store/TutorialStore';
import { APP_CONFIG } from '../../../config/app.config';
import ConnectStoreStep from './ConnectStoreStep';
import ImportProductsStep from './ImportProductsStep';
import FinishedStep from './FinishedStep';
import { ONBOARDING_STEP_CARD_HEIGHT } from '../shared/OnboardingStepCard';
import { useActiveImportJobs } from '@features/connections/hooks/useActiveImportJobs';

type OnboardingFlowStep = 'connect' | 'import' | 'finished';

const STEP_ORDER: OnboardingFlowStep[] = ['connect', 'import', 'finished'];

const DashboardOnboarding: React.FC = () => {
  const { markStepCompleted } = useTutorialStore();
  const { connections } = useConnections();
  const colors = APP_CONFIG.ui.theme.colors;
  const hasConnection = (connections?.length ?? 0) > 0;
  const { jobs: activeJobs, isFetched: activeJobsFetched } = useActiveImportJobs();

  // With a connection, land on import so running jobs (fetched from the API) are
  // visible when revisiting via the tutorial card. ImportProductsStep advances
  // to finished only after the active-jobs query has resolved empty.
  const [step, setStep] = useState<OnboardingFlowStep>(() =>
    hasConnection ? 'import' : 'connect',
  );

  const [slideDirection, setSlideDirection] = useState<1 | -1>(1);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const stepRef = useRef(step);
  stepRef.current = step;

  const goToStep = useCallback((next: OnboardingFlowStep) => {
    const current = stepRef.current;
    if (current === next) return;

    const fromIndex = STEP_ORDER.indexOf(current);
    const toIndex = STEP_ORDER.indexOf(next);
    setSlideDirection(toIndex >= fromIndex ? 1 : -1);
    setShouldAnimate(true);
    setStep(next);
  }, []);

  useEffect(() => {
    if (step !== 'connect') return;
    if (!hasConnection) return;

    markStepCompleted('marketplace-connection');
    goToStep('import');
  }, [goToStep, hasConnection, markStepCompleted, step]);

  // If the user is on the finished step and jobs are still (or newly) running,
  // send them back to the import view.
  useEffect(() => {
    if (!activeJobsFetched) return;
    if (step !== 'finished') return;
    if (activeJobs.length === 0) return;
    goToStep('import');
  }, [activeJobs.length, activeJobsFetched, goToStep, step]);

  return (
    <Stack p={6} bgcolor={colors.background} height="100vh">
      <Header title="Dashboard" />

      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        gap={4}
        alignItems="center"
        justifyContent="center"
        flex={1}
        bgcolor={colors.backgroundTint}
        border={1}
        borderColor={colors.border}
        borderRadius="16px"
        px={{ xs: 3, md: 10 }}
        py={{ xs: 6, md: 10 }}
        mt={2}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 610,
            height: ONBOARDING_STEP_CARD_HEIGHT,
            flexShrink: 0,
          }}
        >
          <Slide
            in
            direction={slideDirection > 0 ? 'left' : 'right'}
            appear={shouldAnimate}
            timeout={{ enter: 550, exit: 350 }}
            easing={{
              enter: 'cubic-bezier(0.23, 1, 0.32, 1)',
              exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            key={step}
          >
            <div>
              {step === 'connect' && (<ConnectStoreStep onContinue={() => goToStep('import')} />)}
              {step === 'import' && <ImportProductsStep onContinue={() => goToStep('finished')} />}
              {step === 'finished' && <FinishedStep onAddConnection={() => goToStep('import')} />}
            </div>
          </Slide>
        </Box>
      </Stack>
    </Stack>
  );
};

export default DashboardOnboarding;
